import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../context/AuthContext';
import { getCurrentCompanyName, getCurrentUserName } from '../../utils/auth';


const Header = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Get company and user info from auth utils
  const companyName = getCurrentCompanyName();
  const userName = getCurrentUserName();

  const navigationItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Chart of Accounts', path: '/chart-of-accounts', icon: 'ListTree' },
    { label: 'Invoices', path: '/invoices', icon: 'FileText' },
    { label: 'Customers', path: '/customer-management', icon: 'Users' },
    { label: 'Expenses', path: '/expenses', icon: 'Receipt' },
    { label: 'Bank Reconciliation', path: '/bank-reconciliation', icon: 'Landmark' },
    { label: 'Financial Reports', path: '/financial-reports', icon: 'BarChart3' },
    { label: 'Financial Insights', path: '/financial-insights', icon: 'Sparkles' },
    { label: 'Journal Entries', path: '/journal-entries', icon: 'BookOpen' },
    { label: 'General Ledger', path: '/general-ledger', icon: 'Book' },
    { label: 'Tax Management', path: '/tax-management', icon: 'Calculator' },
    { label: 'User Management', path: '/user-management', icon: 'UserCog' },
    { label: 'Company Management', path: '/company-management', icon: 'Building2' },
    { label: 'Settings', path: '/settings', icon: 'Settings' }
  ];

  const notifications = [
    { id: 1, title: 'Invoice #1234 Overdue', message: 'Payment is 5 days overdue', time: '2 hours ago', type: 'warning' },
    { id: 2, title: 'Bank Reconciliation Required', message: 'January 2026 needs reconciliation', time: '1 day ago', type: 'error' },
    { id: 3, title: 'New Expense Submitted', message: 'Office supplies - $234.50', time: '2 days ago', type: 'success' }
  ];

  const isActive = (path) => location?.pathname === path;

  const handleNotificationClick = () => {
    setNotificationsOpen(!notificationsOpen);
    setUserMenuOpen(false);
  };

  const handleUserMenuClick = () => {
    setUserMenuOpen(!userMenuOpen);
    setNotificationsOpen(false);
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 h-16 bg-card shadow-md z-1000 transition-smooth">
        <div className="h-full px-6 flex items-center gap-4">
          <div className="flex items-center gap-4 flex-shrink-0">
            <Link to="/dashboard" className="flex items-center gap-3 hover-lift">
              <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center transition-smooth">
                <Icon name="Calculator" size={24} color="var(--color-primary)" />
              </div>
              <span className="font-heading font-semibold text-xl text-foreground hidden sm:block">
                {companyName}
              </span>
            </Link>
          </div>

            <nav className="hidden md:block flex-1 max-w-4xl relative">
              <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide pb-1 scroll-smooth">
                {navigationItems?.map((item) => (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    className={`
                      px-4 py-2 rounded-md font-body font-medium text-sm transition-smooth
                      flex items-center gap-2 hover-lift active-press whitespace-nowrap flex-shrink-0
                      ${isActive(item?.path)
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-foreground hover:bg-muted'
                      }
                    `}
                  >
                    <Icon name={item?.icon} size={18} />
                    <span>{item?.label}</span>
                  </Link>
                ))}
              </div>
              {/* Gradient fade indicators */}
              <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-card to-transparent pointer-events-none"></div>
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-card to-transparent pointer-events-none"></div>
            </nav>

          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="relative">
              <button
                onClick={handleNotificationClick}
                className="relative p-2 rounded-md hover:bg-muted transition-smooth hover-lift active-press"
                aria-label="Notifications"
              >
                <Icon name="Bell" size={20} />
                {notifications?.length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-error rounded-full"></span>
                )}
              </button>

              {notificationsOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-popover rounded-lg shadow-xl border border-border z-1010 transition-smooth">
                  <div className="p-4 border-b border-border">
                    <h3 className="font-heading font-semibold text-base">Notifications</h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications?.map((notification) => (
                      <div
                        key={notification?.id}
                        className="p-4 border-b border-border hover:bg-muted transition-smooth cursor-pointer"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`
                            w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0
                            ${notification?.type === 'warning' ? 'bg-warning/10' : ''}
                            ${notification?.type === 'error' ? 'bg-error/10' : ''}
                            ${notification?.type === 'success' ? 'bg-success/10' : ''}
                          `}>
                            <Icon
                              name={notification?.type === 'warning' ? 'AlertTriangle' : notification?.type === 'error' ? 'AlertCircle' : 'CheckCircle'}
                              size={16}
                              color={`var(--color-${notification?.type})`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-body font-medium text-sm text-foreground">{notification?.title}</p>
                            <p className="caption text-muted-foreground mt-1">{notification?.message}</p>
                            <p className="caption text-muted-foreground mt-1">{notification?.time}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 text-center border-t border-border">
                    <button className="caption text-primary hover:text-primary/80 transition-smooth">
                      View all notifications
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={handleUserMenuClick}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-smooth hover-lift active-press"
                aria-label="User menu"
              >
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="User" size={18} color="var(--color-primary)" />
                </div>
                <span className="font-body font-medium text-sm text-foreground hidden md:block">{userName}</span>
                <Icon name="ChevronDown" size={16} />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-popover rounded-lg shadow-xl border border-border z-1010 transition-smooth">
                  <div className="p-4 border-b border-border">
                    <p className="font-body font-medium text-sm text-foreground">{userName}</p>
                    <p className="caption text-muted-foreground">{user?.email || 'user@example.com'}</p>
                  </div>
                  <div className="py-2">
                    <button className="w-full px-4 py-2 text-left font-body text-sm text-foreground hover:bg-muted transition-smooth flex items-center gap-2">
                      <Icon name="User" size={16} />
                      <span>Profile</span>
                    </button>
                    <button className="w-full px-4 py-2 text-left font-body text-sm text-foreground hover:bg-muted transition-smooth flex items-center gap-2">
                      <Icon name="Building2" size={16} />
                      <span>Switch Company</span>
                    </button>
                    <button className="w-full px-4 py-2 text-left font-body text-sm text-foreground hover:bg-muted transition-smooth flex items-center gap-2">
                      <Icon name="Settings" size={16} />
                      <span>Settings</span>
                    </button>
                  </div>
                  <div className="py-2 border-t border-border">
                    <button 
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left font-body text-sm text-error hover:bg-muted transition-smooth flex items-center gap-2"
                    >
                      <Icon name="LogOut" size={16} />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={handleMobileMenuToggle}
              className="md:hidden p-2 rounded-md hover:bg-muted transition-smooth hover-lift active-press"
              aria-label="Toggle mobile menu"
            >
              <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>
      {mobileMenuOpen && (
        <div className="fixed inset-0 top-16 bg-background z-1020 md:hidden transition-smooth overflow-y-auto">
          <nav className="p-6 space-y-2">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`
                  block px-4 py-3 rounded-md font-body font-medium text-base transition-smooth
                  flex items-center gap-3 hover-lift active-press
                  ${isActive(item?.path)
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-foreground hover:bg-muted'
                  }
                `}
              >
                <Icon name={item?.icon} size={20} />
                <span>{item?.label}</span>
              </Link>
            ))}
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;